import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { FirebaseContext } from './Firebase';
import { getConsoleDebugInfo } from './ConsoleLogger';
import { getFirebaseFunctionsDebugInfo } from './Firebase/functionsDebug';

const DebugPanelWrapper = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.9);
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 15px;
  border-radius: 8px;
  max-width: 400px;
  max-height: 500px;
  overflow-y: auto;
  z-index: 9999;
  border: 1px solid #00ff00;
  display: ${props => props.show ? 'block' : 'none'};
`;

const DebugButton = styled.button`
  position: fixed;
  top: 10px;
  right: 10px;
  background: #ff4444;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  z-index: 10000;
  font-size: 12px;
`;

const DebugSection = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid #333;
  padding-bottom: 5px;
`;

const DebugTitle = styled.div`
  font-weight: bold;
  color: #ffff00;
  margin-bottom: 5px;
`;

const DebugItem = styled.div`
  margin: 2px 0;
  word-break: break-all;
`;

const CopyButton = styled.button`
  background: #444;
  color: #00ff00;
  border: 1px solid #00ff00;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  margin-top: 10px;
`;

const DebugPanel = () => {
  const [show, setShow] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const { user, firebase, loading } = useContext(FirebaseContext);

  useEffect(() => {
    // Only run in browser (not during SSR)
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      // Capture debug information
      const info = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent || 'Unknown',
        url: window.location.href || 'Unknown',
        firebase: {
          user: user ? {
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            displayName: user.displayName
          } : null,
          loading,
          initialized: !!firebase
        },
        environment: {
          nodeEnv: process.env.NODE_ENV,
          gatsbyEnv: process.env.GATSBY_ENVIRONMENT,
          firebaseApiKey: process.env.GATSBY_FIREBASE_API_KEY ? 'Set' : 'Missing',
          firebaseProjectId: process.env.GATSBY_FIREBASE_PROJECT_ID ? 'Set' : 'Missing',
          strapiEmail: process.env.STRAPI_EMAIL ? 'Set' : 'Missing',
          strapiPassword: process.env.STRAPI_PASSWORD ? 'Set' : 'Missing'
        },
        console: {
          errors: [],
          warnings: []
        }
      };

      // Capture console errors and warnings
      const originalError = console.error;
      const originalWarn = console.warn;
      
      console.error = (...args) => {
        info.console.errors.push({ timestamp: new Date().toISOString(), message: args.join(' ') });
        originalError.apply(console, args);
      };
      
      console.warn = (...args) => {
        info.console.warnings.push({ timestamp: new Date().toISOString(), message: args.join(' ') });
        originalWarn.apply(console, args);
      };

      setDebugInfo(info);
    }
  }, [user, firebase, loading]);

  const copyToClipboard = () => {
    // Check if clipboard API is available
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      alert('Clipboard API not available');
      return;
    }

    const fullDebugInfo = {
      ...debugInfo,
      console: getConsoleDebugInfo(),
      firebaseFunctions: getFirebaseFunctionsDebugInfo()
    };
    const text = JSON.stringify(fullDebugInfo, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      alert('Complete debug info copied to clipboard!');
    }).catch((err) => {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy debug info. Check console for details.');
    });
  };

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      <DebugButton onClick={() => setShow(!show)}>
        {show ? 'Hide Debug' : 'Show Debug'}
      </DebugButton>
      
      <DebugPanelWrapper show={show}>
        <DebugTitle>🐛 Debug Panel</DebugTitle>
        
        <DebugSection>
          <DebugTitle>⏰ Timestamp</DebugTitle>
          <DebugItem>{debugInfo.timestamp}</DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>🌐 Environment</DebugTitle>
          <DebugItem>Node Env: {debugInfo.environment?.nodeEnv}</DebugItem>
          <DebugItem>Gatsby Env: {debugInfo.environment?.gatsbyEnv}</DebugItem>
          <DebugItem>Firebase API Key: {debugInfo.environment?.firebaseApiKey}</DebugItem>
          <DebugItem>Firebase Project ID: {debugInfo.environment?.firebaseProjectId}</DebugItem>
          <DebugItem>Strapi Email: {debugInfo.environment?.strapiEmail}</DebugItem>
          <DebugItem>Strapi Password: {debugInfo.environment?.strapiPassword}</DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>🔥 Firebase</DebugTitle>
          <DebugItem>Initialized: {debugInfo.firebase?.initialized ? '✅' : '❌'}</DebugItem>
          <DebugItem>Loading: {debugInfo.firebase?.loading ? '⏳' : '✅'}</DebugItem>
          <DebugItem>User: {debugInfo.firebase?.user ? '✅' : '❌'}</DebugItem>
          {debugInfo.firebase?.user && (
            <>
              <DebugItem>UID: {debugInfo.firebase.user.uid}</DebugItem>
              <DebugItem>Email: {debugInfo.firebase.user.email}</DebugItem>
              <DebugItem>Verified: {debugInfo.firebase.user.emailVerified ? '✅' : '❌'}</DebugItem>
            </>
          )}
        </DebugSection>

        <DebugSection>
          <DebugTitle>🚨 Console Errors ({debugInfo.console?.errors?.length || 0})</DebugTitle>
          {debugInfo.console?.errors?.slice(-5).map((error, index) => (
            <DebugItem key={index} style={{ color: '#ff6666' }}>
              {error.message.substring(0, 100)}...
            </DebugItem>
          ))}
        </DebugSection>

        <DebugSection>
          <DebugTitle>⚠️ Console Warnings ({debugInfo.console?.warnings?.length || 0})</DebugTitle>
          {debugInfo.console?.warnings?.slice(-5).map((warning, index) => (
            <DebugItem key={index} style={{ color: '#ffaa00' }}>
              {warning.message.substring(0, 100)}...
            </DebugItem>
          ))}
        </DebugSection>

        <CopyButton onClick={copyToClipboard}>
          📋 Copy Debug Info
        </CopyButton>
      </DebugPanelWrapper>
    </>
  );
};

export default DebugPanel;
